import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const SITE_ID = 'melaniebobby'; // This site's identifier

const SiteConfigContext = createContext(null);

// Default config - these values are used when remote config doesn't provide them
const DEFAULT_CONFIG = {
  site_id: 'melaniebobby',
  site_name: 'Melanie Bobby Real Estate',
  tagline: 'Investing in You',
  industry: 'real_estate',
  
  // Contact Info
  contact_info: {
    phone: '605-216-3130',
    email: 'melanie@advnorthern.com',
    address: 'Aberdeen, SD'
  },
  
  // Agent Info
  agents: [{
    name: 'Melanie Bobby',
    title: 'Broker, Century 21 Northern Advantage',
    bio: `I'm Melanie Bobby, a proud Broker with C21 Northern Advantage, and I'm honored to serve clients in Aberdeen and the surrounding communities. My approach to real estate is rooted in the values that guide my life every day - imperfectly following Jesus, loving my family, and a genuine commitment to helping others succeed.

For me, real estate is about far more than properties and transactions. It's about people, relationships, and helping families find the place where their lives will grow and thrive. Whether you're purchasing your first home, upgrading for your growing family, downsizing, or exploring investment opportunities, I'm dedicated to guiding you through the process with honesty, patience, and strong market knowledge.

Real estate is my profession, but serving people is my passion. When you work with me, you're not just another transaction - you're someone I'm committed to investing in.`,
    photo: 'https://customer-assets.emergentagent.com/job_agent-suite-7/artifacts/hxo0e7nl_2406905_normal.jpg'
  }],
  
  // Images
  images: {
    hero: 'https://customer-assets.emergentagent.com/job_agent-suite-7/artifacts/zws7h63r_Option%202.png',
    about_banner: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1920',
    logo: null
  },
  
  // About Section
  about_heading: 'Investing in You',
  about_description: 'Real estate is more than just a transaction—it is about finding the place where your life will unfold. I bring honesty, patience, and deep local knowledge to help you navigate every step of your journey.',
  
  // Legacy flat fields (for backwards compatibility)
  agent_name: 'Melanie Bobby',
  agent_title: 'Broker, Century 21 Northern Advantage',
  agent_photo: 'https://customer-assets.emergentagent.com/job_agent-suite-7/artifacts/hxo0e7nl_2406905_normal.jpg',
  agent_bio: `I'm Melanie Bobby, a proud Broker with C21 Northern Advantage...`,
  company_name: 'Century 21 Northern Advantage',
  phone: '605-216-3130',
  email: 'melanie@advnorthern.com',
  city: 'Aberdeen',
  state: 'SD',
  facebook: 'https://www.facebook.com/melanie.bobby.96/',
  linkedin: 'https://www.linkedin.com/in/melanie-bobby-0a911a156/',
  realtor_com: 'https://www.realtor.com/realestateagents/melanie-bobby_aberdeen_sd_3357870',
  primary_color: '#BEAF87',
  secondary_color: '#1a1a1a',
  mls_search_url: 'https://www.hubcityhomes4sale.com/map/',
  hero_headline: 'Find Your Perfect Home in Aberdeen',
  hero_subheadline: 'Expert guidance for buying and selling in Hub City and surrounding South Dakota communities',
  about_headline: 'Investing in You',
  about_content: 'Real estate is more than just a transaction—it is about finding the place where your life will unfold.',
  market_leader_email: 'melanie@hubcityhomes4sale.com',
};

/**
 * Smart merge: only override a field if the remote value is non-empty.
 * Empty string, null, undefined, or empty array = keep the default value.
 */
function mergeConfig(defaults, remote) {
  if (!remote || typeof remote !== 'object') {
    return defaults;
  }
  
  const merged = { ...defaults };
  
  for (const key in remote) {
    const remoteValue = remote[key];
    
    // Skip if remote value is empty/null/undefined
    if (remoteValue === null || remoteValue === undefined || remoteValue === '') {
      continue;
    }
    
    // Skip if remote value is an empty array
    if (Array.isArray(remoteValue) && remoteValue.length === 0) {
      continue;
    }
    
    // Skip if remote value is an empty object
    if (typeof remoteValue === 'object' && !Array.isArray(remoteValue) && Object.keys(remoteValue).length === 0) {
      continue;
    }
    
    // For nested objects, recursively merge
    if (typeof remoteValue === 'object' && !Array.isArray(remoteValue) && typeof defaults[key] === 'object' && !Array.isArray(defaults[key])) {
      merged[key] = mergeConfig(defaults[key] || {}, remoteValue);
    } else {
      // Override with remote value
      merged[key] = remoteValue;
    }
  }
  
  return merged;
}

export function SiteConfigProvider({ children }) {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        // Fetch from backend (which tries Neptune Monkey first, then local DB)
        const response = await axios.get(`${API}/api/site-config/${SITE_ID}`);
        
        // Smart merge: only override non-empty values
        const mergedConfig = mergeConfig(DEFAULT_CONFIG, response.data);
        setConfig(mergedConfig);
        
        console.log('Site config loaded and merged');
      } catch (err) {
        console.error('Error fetching site config:', err);
        setError(err);
        // Keep default config on error
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  return (
    <SiteConfigContext.Provider value={{ config, loading, error }}>
      {children}
    </SiteConfigContext.Provider>
  );
}

export function useSiteConfig() {
  const context = useContext(SiteConfigContext);
  if (!context) {
    throw new Error('useSiteConfig must be used within a SiteConfigProvider');
  }
  return context;
}

export default SiteConfigContext;
